import "./index.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  type RouteObject,
} from "react-router";
import { AreaRoute } from "./components/area-guard";
import { ThemeProvider } from "./components/theme-provider";
import { Toaster } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { AuthLayout } from "./pages/auth/_layout";
import { ForgotPasswordPage } from "./pages/auth/forgot-password";
import { LoginPage } from "./pages/auth/login";
import { ResetPasswordPage } from "./pages/auth/reset-password";
import { DashboardLayout } from "./pages/dashboard/_layout";
import { DASHBOARD_ROUTES } from "./routes";
import { useAuthStore } from "./stores/auth.store";

const queryClient = new QueryClient();

function NotFound() {
  const user = useAuthStore((s) => s.user);
  return <Navigate to={user ? "/dashboard" : "/auth"} replace />;
}

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <RouterProvider router={router} />
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to="/auth" replace />,
  },
  {
    path: "/auth",
    element: <AuthLayout />,
    children: [
      {
        index: true,
        element: <LoginPage />,
      },
      {
        path: "forgot-password",
        element: <ForgotPasswordPage />,
      },
      {
        path: "reset-password",
        element: <ResetPasswordPage />,
      },
    ],
  },
  {
    path: "/dashboard",
    element: <DashboardLayout />,
    children: DASHBOARD_ROUTES.map((route): RouteObject => {
      const element = (
        <AreaRoute area={route.area} title={route.title}>
          {route.element}
        </AreaRoute>
      );
      return route.path ? { path: route.path, element } : { index: true, element };
    }),
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);

export default App;
